<template>
  <v-container fluid>
    <v-row justify="center">
      <v-col cols="12" lg="11" xl="10">
        <v-alert v-if="!isUserAdmin" type="error" outlined class="mt-8">
          You are not allowed to access creation diagnostics.
        </v-alert>

        <v-card v-else class="mt-8 mb-8" :loading="listLoading">
          <template slot="progress">
            <v-progress-linear color="#006c27" indeterminate height="6" />
          </template>

          <v-card-title class="grey--text text--darken-2">
            Creation Diagnostics
            <v-spacer />
            <v-btn color="#006c27" dark :loading="listLoading" @click="reload">
              Reload
            </v-btn>
          </v-card-title>

          <v-card-text>
            <v-alert dense outlined type="info" border="left" colored-border class="mb-5">
              Assessment reads the current CASS, SAP, and Entra resource state and stores a safe
              snapshot. It does not create, update, or delete external resources. A hold prevents
              another creation request for the owner until it is released.
            </v-alert>

            <v-row dense align="center">
              <v-col cols="12" md="7">
                <v-text-field
                  v-model="search"
                  label="Search operation ID, owner reference, or display name"
                  outlined
                  dense
                  clearable
                  hide-details
                  @keyup.enter="applyFilters"
                />
              </v-col>
              <v-col cols="12" md="2">
                <v-checkbox
                  v-model="failedOnly"
                  label="Failed only"
                  hide-details
                  class="ma-0"
                />
              </v-col>
              <v-col cols="12" md="3" class="d-flex justify-end">
                <v-btn
                  color="#006c27"
                  dark
                  class="mr-2"
                  :loading="listLoading"
                  @click="applyFilters"
                >
                  Search
                </v-btn>
                <v-btn text color="#006c27" :disabled="listLoading" @click="clearFilters">
                  Clear
                </v-btn>
              </v-col>
            </v-row>

            <v-data-table
              :headers="headers"
              :items="items"
              :loading="listLoading"
              item-key="operationId"
              hide-default-footer
              class="elevation-1 mt-5"
              @click:row="openDetail"
            >
              <template v-slot:[`item.status`]="{ item }">
                <v-chip small dark :color="statusColor(item.status)">
                  {{ item.status || "unknown" }}
                </v-chip>
              </template>
              <template v-slot:[`item.held`]="{ item }">
                <v-chip small dark :color="item.held ? '#ef6c00' : '#607d8b'">
                  {{ item.held ? "active" : "none" }}
                </v-chip>
              </template>
              <template v-slot:[`item.createdAt`]="{ item }">
                <span class="text-caption">{{ formatDate(item.createdAt) }}</span>
              </template>
              <template slot="no-data">
                <div v-if="listLoading">Loading operations...</div>
                <div v-else>No creation operations found.</div>
              </template>
            </v-data-table>

            <div class="d-flex justify-end align-center mt-3">
              <v-btn
                text
                color="#006c27"
                :disabled="!canGoPrevious || listLoading"
                @click="previousPage"
              >
                Previous
              </v-btn>
              <span class="text-caption mx-3">Page {{ page }}</span>
              <v-btn
                text
                color="#006c27"
                :disabled="!nextCursor || listLoading"
                @click="nextPage"
              >
                Next
              </v-btn>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-dialog v-model="detailDialog" max-width="1100" persistent>
      <v-card :loading="detailLoading || actionLoading">
        <v-card-title class="text--secondary">
          Creation Operation
          <v-spacer />
          <v-btn icon :disabled="detailLoading || actionLoading" @click="closeDetail">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text v-if="detail">
          <div class="text-subtitle-2 mb-2">Request and Lifecycle</div>
          <v-row dense>
            <v-col cols="12" md="6"><strong>Operation ID:</strong> {{ detail.operationId || "-" }}</v-col>
            <v-col cols="12" md="6"><strong>Request ID:</strong> {{ detail.requestId || "-" }}</v-col>
            <v-col cols="12" md="6"><strong>Owner reference:</strong> {{ detail.ownerRef || "-" }}</v-col>
            <v-col cols="12" md="6"><strong>Display name:</strong> {{ detail.displayName || "-" }}</v-col>
            <v-col cols="12" md="6"><strong>Requested environment:</strong> {{ request.environment || "-" }}</v-col>
            <v-col cols="12" md="6"><strong>Requested permission model:</strong> {{ request.permissionModel || "-" }}</v-col>
            <v-col cols="12" md="6"><strong>Status:</strong> {{ detail.status || "-" }}</v-col>
            <v-col cols="12" md="6"><strong>Progress:</strong> {{ detail.progress || "-" }}</v-col>
            <v-col cols="12" md="6"><strong>Attempts:</strong> {{ detail.attempts }}</v-col>
            <v-col cols="12" md="6"><strong>Creation hold:</strong> {{ detail.held ? "Active" : "Not active" }}</v-col>
            <v-col cols="12" md="6"><strong>Created:</strong> {{ formatDate(detail.createdAt) }}</v-col>
            <v-col cols="12" md="6"><strong>Started:</strong> {{ formatDate(detail.startedAt) }}</v-col>
            <v-col cols="12" md="6"><strong>Completed:</strong> {{ formatDate(detail.completedAt) }}</v-col>
            <v-col cols="12" md="6"><strong>Next attempt:</strong> {{ formatDate(detail.nextAttemptAt) }}</v-col>
          </v-row>

          <v-alert v-if="detail.failure" dense outlined type="error" class="mt-4">
            <strong>{{ detail.failure.code || "Creation failure" }}:</strong>
            {{ detail.failure.message || "-" }}
          </v-alert>

          <div class="mt-5">
            <div class="text-subtitle-2 mb-2">Progress Messages</div>
            <v-alert v-if="messages.length === 0" dense outlined type="info">
              No progress messages were recorded.
            </v-alert>
            <v-simple-table v-else dense class="elevation-1">
              <thead>
                <tr><th>Time</th><th>Stage</th><th>Status</th><th>Message</th></tr>
              </thead>
              <tbody>
                <tr v-for="(message, index) in messages" :key="index">
                  <td>{{ formatDate(message.at) }}</td>
                  <td>{{ message.stage || "-" }}</td>
                  <td>{{ message.status || "-" }}</td>
                  <td>{{ message.message || "-" }}</td>
                </tr>
              </tbody>
            </v-simple-table>
          </div>

          <v-divider class="my-5" />

          <div class="text-subtitle-2 mb-2">Resource Checkpoints</div>
          <v-simple-table dense class="elevation-1">
            <thead>
              <tr><th>CASS application</th><th>SAP application</th><th>Entra object ID</th><th>Entra client ID</th><th>Service principal ID</th></tr>
            </thead>
            <tbody>
              <tr>
                <td>{{ detail.cassApplicationId || "-" }}</td>
                <td>{{ detail.sapApplicationId || "-" }}</td>
                <td>{{ detail.entraApplicationObjectId || "-" }}</td>
                <td>{{ detail.entraApplicationClientId || "-" }}</td>
                <td>{{ detail.servicePrincipalId || "-" }}</td>
              </tr>
            </tbody>
          </v-simple-table>

          <v-divider class="my-5" />

          <div class="d-flex align-center mb-2">
            <div class="text-subtitle-2">Resource Assessment</div>
            <v-spacer />
            <v-btn small color="#006c27" dark :loading="actionLoading" @click="assess">
              Recheck
            </v-btn>
          </div>
          <div class="text-caption grey--text mb-3">
            Recheck only refreshes the recorded assessment. It never changes external resources.
          </div>
          <v-alert v-if="!detail.assessment" dense outlined type="info">
            No resource assessment has been recorded yet. Use Recheck to collect a safe current-state snapshot.
          </v-alert>
          <template v-else>
            <v-alert dense outlined type="info" class="mb-3">
              <strong>Policy:</strong> {{ detail.assessment.policy || "-" }}.
              {{ assessmentPolicyExplanation }}
            </v-alert>
            <div class="text-caption grey--text mb-2">
              Assessed {{ formatDate(detail.assessment.at) }}.
            </div>
            <v-simple-table dense class="elevation-1">
              <thead>
                <tr>
                  <th>Resource</th><th>State</th><th>Identifier</th><th>Client ID</th><th>Review</th><th>Detail</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="resource in resources" :key="resource.name">
                  <td>{{ resource.name }}</td>
                  <td>
                    <v-chip small dark :color="resourceColor(resource.value.state)">
                      {{ resourceStateText(resource.value.state) }}
                    </v-chip>
                  </td>
                  <td>{{ resource.value.id || checkpointFor(resource.key) || "-" }}</td>
                  <td>{{ resource.value.clientId || (resource.key === "entraApplication" ? detail.entraApplicationClientId : "-") || "-" }}</td>
                  <td>
                    <v-btn
                      v-if="developerPortalLink(resource)"
                      x-small
                      text
                      color="#006c27"
                      :href="developerPortalLink(resource)"
                      target="_blank"
                    >
                      SAP Portal
                    </v-btn>
                    <v-btn
                      v-if="azurePortalLink(resource)"
                      x-small
                      text
                      color="#006c27"
                      :href="azurePortalLink(resource)"
                      target="_blank"
                    >
                      Azure Portal
                    </v-btn>
                    <span v-if="!developerPortalLink(resource) && !azurePortalLink(resource)">-</span>
                  </td>
                  <td class="text-caption">{{ resource.value.detail || "-" }}</td>
                </tr>
              </tbody>
            </v-simple-table>
          </template>

          <v-divider class="my-5" />

          <div class="text-subtitle-2 mb-2">Hold Management</div>
          <v-alert dense outlined type="warning" border="left" colored-border>
            Releasing a hold only allows the owner to make another creation request. It does not delete
            or modify SAP, Entra, CASS, or any other external resource.
          </v-alert>
          <v-btn
            v-if="detail.held"
            color="#ef6c00"
            dark
            :disabled="actionLoading"
            @click="openReleaseDialog"
          >
            Release Hold
          </v-btn>
          <div v-else class="text-caption grey--text">
            No active hold. This record can be permanently deleted if it is no longer needed.
          </div>

          <v-divider class="my-5" />

          <div class="text-subtitle-2 mb-2">Audit History</div>
          <v-alert v-if="audits.length === 0" dense outlined type="info">No audit records found.</v-alert>
          <v-simple-table v-else dense class="elevation-1 mb-3">
            <thead>
              <tr><th>Time</th><th>Action</th><th>Actor</th><th>Result</th><th>Reason</th></tr>
            </thead>
            <tbody>
              <tr v-for="(audit, index) in audits" :key="index">
                <td>{{ formatDate(audit.at) }}</td>
                <td>{{ audit.action || "-" }}</td>
                <td>{{ audit.actor || "-" }}</td>
                <td>{{ audit.result || "-" }}</td>
                <td>{{ audit.reason || "-" }}</td>
              </tr>
            </tbody>
          </v-simple-table>

          <div class="d-flex align-center mt-4">
            <div class="text-subtitle-2">Safe Diagnostics</div>
            <v-spacer />
            <v-btn text small color="#006c27" @click="showRaw = !showRaw">
              {{ showRaw ? "Hide" : "Show" }} raw safe diagnostics
            </v-btn>
          </div>
          <div v-if="showRaw">
            <div class="text-caption grey--text mb-1">
              The backend redacts sensitive values before returning these records.
            </div>
            <pre class="detail-json">{{ safeJson(detail.diagnostics || []) }}</pre>
          </div>
        </v-card-text>

        <v-card-actions>
          <v-btn
            v-if="detail && !detail.held"
            text
            color="#b71c1c"
            :disabled="actionLoading"
            @click="openDeleteDialog"
          >
            Delete Record
          </v-btn>
          <v-spacer />
          <v-btn text :disabled="actionLoading" @click="closeDetail">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="releaseDialog" max-width="620">
      <v-card>
        <v-card-title class="text--secondary">Release Creation Hold</v-card-title>
        <v-card-text>
          <v-alert dense outlined type="warning">
            This action only permits another user request. It does not delete or alter external resources.
          </v-alert>
          <v-textarea v-model="releaseReason" label="Optional note" outlined rows="2" auto-grow />
          <v-checkbox
            v-model="releaseConfirmed"
            color="#ef6c00"
            label="I understand this releases only the creation hold."
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text :disabled="actionLoading" @click="releaseDialog = false">Cancel</v-btn>
          <v-btn color="#ef6c00" dark :loading="actionLoading" :disabled="!releaseConfirmed" @click="release">
            Release Hold
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="deleteDialog" max-width="620">
      <v-card>
        <v-card-title class="text--secondary">Permanently Delete Record</v-card-title>
        <v-card-text>
          <v-alert dense outlined type="error">
            This permanently deletes this diagnostics record and its audit history. It cannot be undone
            and does not delete external resources.
          </v-alert>
          <v-checkbox
            v-model="deleteConfirmed"
            color="#b71c1c"
            label="I understand this permanent deletion cannot be undone."
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text :disabled="actionLoading" @click="deleteDialog = false">Cancel</v-btn>
          <v-btn color="#b71c1c" dark :loading="actionLoading" :disabled="!deleteConfirmed" @click="remove">
            Delete Permanently
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar.visible" :color="snackbar.color" timeout="4000">
      {{ snackbar.text }}
    </v-snackbar>
  </v-container>
</template>

<script>
import cass from "../api/cass";
import { getDeveloperPortalUrl } from "../utils/developerPortal";

const POLICY_EXPLANATIONS = {
  release_clean_failure: "No resources remain, so the hold is released automatically.",
  release_survived_creation: "CASS and all external resources exist, so the hold is released automatically.",
  release_alert: "CASS exists. The hold is released automatically, and API CoE repair review is required for inconsistent or unavailable external dependencies.",
  retain_alert: "CASS is missing while external resources are found or unknown. The hold remains to prevent duplicate resources until API CoE repair review is complete.",
};

export default {
  name: "CreationDiagnostics",
  data() {
    return {
      listLoading: false,
      detailLoading: false,
      actionLoading: false,
      search: "",
      failedOnly: false,
      items: [],
      nextCursor: "",
      cursors: [""],
      page: 1,
      pageSize: 25,
      detailDialog: false,
      detail: null,
      releaseDialog: false,
      releaseReason: "",
      releaseConfirmed: false,
      deleteDialog: false,
      deleteConfirmed: false,
      showRaw: false,
      snackbar: { visible: false, color: "#006c27", text: "" },
      headers: [
        { text: "Created", value: "createdAt", sortable: false },
        { text: "Operation ID", value: "operationId", sortable: false },
        { text: "Owner", value: "ownerRef", sortable: false },
        { text: "Display Name", value: "displayName", sortable: false },
        { text: "Status", value: "status", sortable: false },
        { text: "Hold", value: "held", sortable: false },
      ],
    };
  },
  computed: {
    isUserAdmin() {
      return !!(this.$userData && this.$userData.cass && this.$userData.cass.isUserAdmin);
    },
    canGoPrevious() {
      return this.page > 1;
    },
    request() {
      return (this.detail && this.detail.request) || {};
    },
    messages() {
      return Array.isArray(this.detail && this.detail.messages) ? this.detail.messages : [];
    },
    audits() {
      return Array.isArray(this.detail && this.detail.audits) ? this.detail.audits : [];
    },
    resources() {
      const assessment = (this.detail && this.detail.assessment) || {};
      return [
        { name: "CASS application", key: "cass", value: assessment.cass || {} },
        { name: "SAP application", key: "sap", value: assessment.sap || {} },
        { name: "Entra application", key: "entraApplication", value: assessment.entraApplication || {} },
        { name: "Service principal", key: "servicePrincipal", value: assessment.servicePrincipal || {} },
      ];
    },
    assessmentPolicyExplanation() {
      const policy = this.detail && this.detail.assessment && this.detail.assessment.policy;
      return POLICY_EXPLANATIONS[policy] || "Review the assessment and recorded hold state.";
    },
  },
  watch: {
    "$route.params.operationId"(operationId) {
      if (this.isUserAdmin && operationId) {
        this.loadDetail(operationId);
      }
    },
  },
  mounted() {
    if (!this.isUserAdmin) {
      return;
    }
    this.loadList();
    if (this.$route.params.operationId) {
      this.loadDetail(this.$route.params.operationId);
    }
  },
  methods: {
    formatDate(value) {
      if (!value) {
        return "-";
      }
      const date = new Date(value);
      return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
    },
    statusColor(status) {
      const value = String(status || "").toLowerCase();
      if (value === "failed") {
        return "#b71c1c";
      }
      if (value === "succeeded") {
        return "#2e7d32";
      }
      return "#ef6c00";
    },
    resourceColor(state) {
      const value = String(state || "").toLowerCase();
      if (value === "found") {
        return "#2e7d32";
      }
      if (value === "missing") {
        return "#b71c1c";
      }
      if (value === "inconsistent") {
        return "#ef6c00";
      }
      return "#607d8b";
    },
    resourceStateText(state) {
      const value = String(state || "unknown").toLowerCase();
      if (value === "found") {
        return "Exists";
      }
      if (value === "missing") {
        return "Missing";
      }
      if (value === "inconsistent") {
        return "Inconsistent";
      }
      return "Unknown";
    },
    checkpointFor(resourceKey) {
      const checkpoints = {
        cass: this.detail.cassApplicationId,
        sap: this.detail.sapApplicationId,
        entraApplication: this.detail.entraApplicationObjectId,
        servicePrincipal: this.detail.servicePrincipalId,
      };
      return checkpoints[resourceKey] || "";
    },
    developerPortalLink(resource) {
      if (resource.key !== "sap") {
        return "";
      }
      const sapApplicationId = this.detail.sapApplicationId || resource.value.id;
      const baseUrl = getDeveloperPortalUrl(this.request.environment);
      return baseUrl && sapApplicationId ? `${baseUrl}application/${sapApplicationId}` : "";
    },
    azurePortalLink(resource) {
      if (resource.key !== "entraApplication") {
        return "";
      }
      const clientId = this.detail.entraApplicationClientId || resource.value.clientId;
      if (!clientId) {
        return "";
      }
      return `https://portal.azure.com/#blade/Microsoft_AAD_RegisteredApps/ApplicationMenuBlade/Overview/appId/${clientId}`;
    },
    safeJson(value) {
      try {
        return JSON.stringify(value, null, 2);
      } catch (_error) {
        return "Unable to display records.";
      }
    },
    notify(text, color = "#006c27") {
      this.snackbar = { visible: true, color, text };
    },
    currentCursor() {
      return this.cursors[this.page - 1] || "";
    },
    async loadList() {
      if (!this.isUserAdmin) {
        return;
      }
      this.listLoading = true;
      try {
        const response = await cass.getAdminApplicationCreationOperations({
          search: this.search,
          failedOnly: this.failedOnly,
          cursor: this.currentCursor(),
          limit: this.pageSize,
        });
        this.items = response.data.items || [];
        this.nextCursor = response.data.nextCursor || "";
      } catch (error) {
        this.notify(cass.handleError(error).message || "Could not load creation operations", "#b71c1c");
      } finally {
        this.listLoading = false;
      }
    },
    reload() {
      this.loadList();
    },
    applyFilters() {
      this.cursors = [""];
      this.page = 1;
      this.loadList();
    },
    clearFilters() {
      this.search = "";
      this.failedOnly = false;
      this.applyFilters();
    },
    nextPage() {
      if (!this.nextCursor) {
        return;
      }
      this.cursors.push(this.nextCursor);
      this.page += 1;
      this.loadList();
    },
    previousPage() {
      if (this.page <= 1) {
        return;
      }
      this.page -= 1;
      this.loadList();
    },
    async openDetail(item) {
      if (item && item.operationId) {
        await this.navigateToDetail(item.operationId);
      }
    },
    async navigateToDetail(operationId) {
      if (this.$route.params.operationId !== operationId) {
        await this.$router.push({ name: "CreationDiagnostics", params: { operationId } });
        return;
      }
      await this.loadDetail(operationId);
    },
    async loadDetail(operationId) {
      if (!this.isUserAdmin || !operationId) {
        return;
      }
      this.detailLoading = true;
      this.detailDialog = true;
      try {
        const response = await cass.getAdminApplicationCreationOperation(operationId);
        this.detail = response.data || null;
        this.showRaw = false;
      } catch (error) {
        this.detailDialog = false;
        this.notify(cass.handleError(error).message || "Could not load creation operation", "#b71c1c");
      } finally {
        this.detailLoading = false;
      }
    },
    closeDetail() {
      this.detailDialog = false;
      if (this.$route.params.operationId) {
        this.$router.push({ name: "CreationDiagnostics" }).catch(() => {});
      }
    },
    async assess() {
      if (!this.isUserAdmin || !this.detail || !this.detail.operationId) {
        return;
      }
      this.actionLoading = true;
      try {
        const response = await cass.assessAdminApplicationCreationOperation(this.detail.operationId);
        this.detail = response.data || this.detail;
        this.notify("Resource assessment refreshed. No external resources were changed.");
        await this.loadList();
      } catch (error) {
        this.notify(cass.handleError(error).message || "Could not recheck resources", "#b71c1c");
      } finally {
        this.actionLoading = false;
      }
    },
    openReleaseDialog() {
      this.releaseReason = "";
      this.releaseConfirmed = false;
      this.releaseDialog = true;
    },
    async release() {
      if (!this.isUserAdmin || !this.detail || !this.detail.held || !this.releaseConfirmed) {
        return;
      }
      this.actionLoading = true;
      try {
        const response = await cass.releaseAdminApplicationCreationOperation(
          this.detail.operationId,
          this.releaseReason
        );
        this.detail = response.data || this.detail;
        this.releaseDialog = false;
        this.notify("Creation hold released. External resources were not changed.");
        await this.loadList();
      } catch (error) {
        this.notify(cass.handleError(error).message || "Could not release hold", "#b71c1c");
      } finally {
        this.actionLoading = false;
      }
    },
    openDeleteDialog() {
      this.deleteConfirmed = false;
      this.deleteDialog = true;
    },
    async remove() {
      if (!this.isUserAdmin || !this.detail || this.detail.held || !this.deleteConfirmed) {
        return;
      }
      this.actionLoading = true;
      try {
        await cass.deleteAdminApplicationCreationOperation(this.detail.operationId);
        this.deleteDialog = false;
        this.detailDialog = false;
        this.detail = null;
        this.notify("Creation diagnostics record permanently deleted.");
        if (this.$route.params.operationId) {
          await this.$router.push({ name: "CreationDiagnostics" });
        }
        await this.loadList();
      } catch (error) {
        this.notify(cass.handleError(error).message || "Could not delete creation operation", "#b71c1c");
      } finally {
        this.actionLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.detail-json {
  max-height: 300px;
  overflow: auto;
  background: #f5f5f5;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 12px;
  font-size: 12px;
  line-height: 1.4;
}

.v-btn {
  text-transform: none;
}
</style>
